import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

type ErrorType = "" | "mismatch" | "duplicate" | "generic";

export function SignupPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorType, setErrorType] = useState<ErrorType>("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [orgSlug, setOrgSlug] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorType("");
    if (password && confirmPassword !== password) {
      setErrorType("mismatch");
      return;
    }
    setLoading(true);
    try {
      const body: Record<string, string> = { email, org_name: orgName, name };
      if (password) body.password = password;
      const data = await api<{ org_slug?: string }>("/api/v2/auth/signup", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setOrgSlug(data?.org_slug || "");
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrorType("duplicate");
      } else {
        setErrorType("generic");
      }
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t("signup.checkEmailTitle")}</CardTitle>
            <CardDescription>
              <Trans i18nKey="signup.verificationSent" values={{ email }}>
                We sent a verification link to <strong>{email}</strong>
              </Trans>
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("signup.verificationInstructions")}
            </p>
            {orgSlug && (
              <div className="rounded-md bg-muted p-3 text-left space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("signup.orgSlugLabel")}
                </p>
                <p className="font-mono text-sm font-semibold">{orgSlug}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {t("signup.shareWithTeam")}
                </p>
                <code className="text-xs bg-background rounded px-2 py-1 block">
                  rai connect {orgSlug}
                </code>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {t("signup.didntReceive")}{" "}
              <Link
                to="/login"
                className="text-foreground underline hover:no-underline"
              >
                {t("signup.tryLoggingIn")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("signup.title")}</CardTitle>
          <CardDescription>
            {t("signup.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("signup.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="orgName">{t("signup.orgName")}</Label>
              <Input
                id="orgName"
                type="text"
                placeholder="Acme Engineering"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                minLength={2}
                maxLength={100}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t("signup.yourName")}</Label>
              <Input
                id="name"
                type="text"
                placeholder="Jane Dev"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={1}
                maxLength={100}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("signup.password")}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("signup.confirmPassword")}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>
            {errorType && (
              <p className="text-sm text-destructive">
                {errorType === "mismatch" && t("signup.passwordMismatch")}
                {errorType === "duplicate" && t("signup.emailAlreadyRegistered")}
                {errorType === "generic" && t("signup.genericError")}{" "}
                {errorType === "duplicate" && (
                  <Link to="/login" className="underline">
                    {t("signup.logInInstead")}
                  </Link>
                )}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  {t("signup.creating")}
                </>
              ) : (
                t("signup.submit")
              )}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {t("signup.alreadyHaveAccount")}{" "}
              <Link to="/login" className="underline hover:text-foreground">
                {t("signup.logIn")}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
